import React from 'react'

import { Input, Select, TextArea, Form } from 'semantic-ui-react'

const FormInput = ({
  label,
  error,
  as = 'input',
  required,
  children,
  type,
  fluid = true,
  leftIcon,
  rightIcon,
  icon,
  iconPosition,
  renderCustomInput,
  className,
  options,
  name,
  value,
  placeholder,
  disabled,
  onChange,
  ...props
}) => {
  // Create a function to generate input classes with icon support
  const getInputClasses = (error, hasLeftIcon, hasRightIcon) => {
    const baseClasses = `block rounded-md border-0 outline-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-teal focus:border-brand-teal focus:outline-0 text-base ${error
      ? 'ring-1 ring-inset ring-red-300'
      : 'ring-1 ring-inset ring-gray-300'
      }`

    // Add padding based on icons
    const paddingClasses = [
      hasLeftIcon ? 'pl-10' : 'pl-3',
      hasRightIcon ? 'pr-10' : 'pr-3',
      'py-2'
    ].join(' ')

    return `${baseClasses} ${paddingClasses}`
  }

  const handleKeyPress = e => {
    // Allow only numbers, decimal point, backspace, delete, and arrow keys for number inputs
    if (type === 'number') {
      const allowedKeys = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '.',
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab'
      ]
      // Allow only one decimal point
      if (e.key === '.' && e.target.value.includes('.')) {
        e.preventDefault()
        return
      }
      // Prevent other non-numeric characters
      if (!allowedKeys.includes(e.key)) {
        e.preventDefault()
      }
    }
  }

  const handleChangeAdapter = (e, data) => {
    if (onChange) {
      if (as === 'select' && data) {
        const syntheticEvent = {
          target: { name: name, value: data.value }
        }
        onChange(syntheticEvent)
      } else if (type === 'number' && e && e.target) {
        const modifiedEvent = {
          ...e,
          target: { ...e.target, type: 'number' }
        }
        onChange(modifiedEvent)
      } else if (e && e.target) {
        onChange(e)
      }
    }
  }

  // Create an error node with support for multi-line errors
  const formatErrorMessage = errorMessage => {
    if (!errorMessage) return null

    // If the error message contains new lines, split it and render with line breaks
    if (errorMessage.includes('\n')) {
      return (
        <div className='mt-1 text-sm text-red-600 whitespace-pre-line'>
          {errorMessage}
        </div>
      )
    }

    // Otherwise, render as normal
    return <p className='mt-1 text-sm text-red-600'>{errorMessage}</p>
  }

  // Props for SUI components or standard inputs
  const suiAndInputProps = {
    name,
    value,
    placeholder,
    disabled,
    fluid,
    icon,
    iconPosition,
    onKeyDown: type === 'number' ? handleKeyPress : undefined,
    type: type === 'number' ? 'text' : type,
    ...props
  }

  // Create label node
  const labelNode = label ? (
    <label className='block text-label font-medium text-gray-700 mb-2'>
      {label} {required && <span className='text-red-500 ml-1'>*</span>}
    </label>
  ) : null

  // Create error node
  const errorNode = error ? formatErrorMessage(error) : null

  // If a custom render function is provided, use it (for backward compatibility)
  if (renderCustomInput) {
    const inputPropsForCustom = {
      ...suiAndInputProps,
      className: className,
      onChange: handleChangeAdapter,
    }
    return renderCustomInput({ inputProps: inputPropsForCustom, labelNode, errorNode })
  }

  // Default rendering with icon support
  const renderInput = () => {
    if (as === 'select') {
      return (
        <Select
          {...suiAndInputProps}
          onChange={handleChangeAdapter}
          options={options}
          className={className}
          search
        >
          {children}
        </Select>
      )
    } else if (as === 'textarea') {
      return (
        <Form>
          <TextArea
            {...suiAndInputProps}
            onChange={handleChangeAdapter}
            className={className}
          />
        </Form>
      )
    } else {
      // Standard input
      return (
        <Input
          {...suiAndInputProps}
          onChange={handleChangeAdapter}
          className={className}
        />
      )
    }
  }

  // Return the final component
  return (
    <div>
      {labelNode}
      {renderInput()}
      {errorNode}
    </div>
  )
}

export default FormInput
